<template>
  <div class="new-team">
    <organization-breadcrumb
      :items="[
        $t($i18nLabelsHelper(currentOrganizationType, 'groups')),
        $t($i18nLabelsHelper(currentOrganizationType, 'add_to_group')),
      ]"
    />
    <div id="create-class-page">
      <el-row type="flex" justify="center">
        <el-col :xs="24">
          <h1 class="blue-color" v-if="createClassSteps.active !== 5">
            {{ $t($i18nLabelsHelper(currentOrganizationType, "add_to_group")) }}
          </h1>
          <h1 class="blue-color" v-else>{{ $t($i18nLabelsHelper(currentOrganizationType, "group_overview")) }}</h1>
          <div>
            <div class="class-create-main">
              <class-create-details
                ref="classdetail"
                v-if="createClassSteps.active >= 1 && createClassSteps.active < 5"
                :currentStep="createClassSteps.active"
                :componentStep="1"
                v-on:next-step="handleNextStep"
                v-on:previous-step="handlePreviousStep"
                :isAdmin="true"
                :editMode="editMode"
                :organizationType="currentOrganizationType"
              />
              <hr v-if="createClassSteps.active >= 2 && createClassSteps.active < 5" class="line-separate" />
              <class-goals
                ref="classgoals"
                v-if="createClassSteps.active >= 2 && createClassSteps.active < 5"
                :currentStep="createClassSteps.active"
                :componentStep="2"
                :editMode="editMode"
                v-on:next-step="handleNextStep"
                v-on:previous-step="handlePreviousStep"
              />
              <hr v-if="createClassSteps.active >= 3 && createClassSteps.active < 5" class="line-separate" />
              <add-learners
                v-if="createClassSteps.active >= 3 && createClassSteps.active < 5"
                ref="addusers"
                :addLearners="buildingClass"
                :componentStep="3"
                :currentStep="createClassSteps.active"
                :isOrganizationUseDummyEmail="false"
                :organizationId="currentOrganizationId"
                :organizationType="currentOrganizationType"
                v-on:next-step="handleNextStep"
                v-on:previous-step="handlePreviousStep"
                v-on:update-add-learner="updateBuildingClass"
              />
              <hr v-if="createClassSteps.active >= 4 && createClassSteps.active < 5" class="line-separate" />
              <assign-license-learners
                ref="assignlicense"
                v-if="createClassSteps.active >= 4 && createClassSteps.active < 5"
                :currentStep="createClassSteps.active"
                :componentStep="4"
                :editMode="editMode"
                v-on:next-step="handleNextStep"
                v-on:previous-step="handlePreviousStep"
                v-on:update-assign-license="updateBuildingClass"
                :assignmentLicense="buildingClass"
              />
              <class-overview
                v-if="createClassSteps.active >= 5"
                :currentStep="createClassSteps.active"
                :componentStep="5"
                v-on:next-step="handleNextStep"
                v-on:edit-step="handleEdit"
                :classDetails="buildingClass"
                :organizationType="currentOrganizationType"
                :uploadId.sync="uploadId"
                :organizationId="currentOrganizationId"
              />
            </div>
            <class-create-footer
              v-if="createClassSteps.active >= 1 && createClassSteps.active < 5"
              :primaryPreviousActive="createClassSteps.active > 1"
              :primaryAction="handleClickContinute"
              :primaryPreviousAction="handlePreviousStep"
              :primaryText="$t('common.continue')"
              :previousText="$t('classes.create_class_flow.previous')"
            />
          </div>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script>
import ClassCreateDetails from "@/components/classes/create-classes/ClassCreateDetails";
import AddLearners from "@/components/modals/organizations/invite-learners/AddLearners";
import ClassGoals from "@/components/classes/create-classes/ClassGoals";
import ClassOverview from "@/components/classes/create-classes/ClassOverview";
import ClassCreateFooter from "@/components/classes/create-classes/ClassCreateFooter";
import { mapGetters, mapActions } from "vuex";
import { successMessage } from "@/utils/messages";
import OrganizationBreadcrumb from "@/components/common/OrganizationBreadcrumb";
import AssignLicenseLearners from "@/components/modals/organizations/invite-learners/AssignLicenseLearners";

export default {
  components: {
    ClassCreateDetails,
    AddLearners,
    ClassGoals,
    ClassOverview,
    ClassCreateFooter,
    AssignLicenseLearners,
    OrganizationBreadcrumb,
  },
  data() {
    return {
      modals: {
        showInviteTeacherModal: false,
      },
      uploadId: null,
      inviteMode: null,
      editMode: false,
      createClassSteps: {
        active: 1,
        steps: [
          {
            title: this.$t("classes.create_class_flow.details"),
          },
          {
            title: this.$t(this.$i18nLabelsHelper(this.currentOrganizationType, "students")),
          },
        ],
      },
    };
  },
  computed: {
    ...mapGetters({
      buildingClass: "classes/getBuildingClass",
      currentOrganizationType: "organization/currentOrganizationType",
      currentOrganizationId: "organization/currentOrganizationId",
    }),
  },

  watch: {
    "createClassSteps.active"() {
      this.scrollIntoCurrentView();
    },
  },

  destroyed() {
    this.resetBuildingClass();
  },

  methods: {
    ...mapActions({
      createClassAction: "classes/createClass",
      updateBuildingClass: "classes/updateBuildingClass",
      resetBuildingClass: "classes/resetBuildingClass",
    }),
    handleClickContinute() {
      switch (this.createClassSteps.active) {
        case 1:
          this.$refs.classdetail.handleContinueClick();
          break;
        case 2:
          this.$refs.classgoals.setGoals();
          break;
        case 3:
          this.$refs.addusers.handleClickContinute();
          break;
        case 4:
          this.$refs.assignlicense.handleClickContinute();
          break;
        default:
          return;
      }
    },
    handleNextStep(activeStep, options = {}) {
      this.createClassSteps.active = activeStep + 1;
      if (options.mode === "file-import") {
        this.uploadId = options.uploadId;
        this.inviteMode = options.mode;
      }
      if (this.createClassSteps.active > 5) {
        successMessage(this.$t(this.$i18nLabelsHelper(this.currentOrganizationType, "your_group_was_created")))(this);

        return this.$router.push({
          name: "organizationteam",
          params: {
            organizationId: this.currentOrganizationId,
          },
        });
      }
    },
    handlePreviousStep() {
      const activeStep = this.createClassSteps.active;
      this.createClassSteps.active = activeStep - 1;
    },
    handleEdit() {
      this.createClassSteps.active = 3;
      this.editMode = true;
    },
    handleCancel() {
      this.$store.dispatch("classes/resetBuildingClass");
      return this.$router.push({
        name: "classes",
        params: {
          organizationId: this.currentOrganizationId,
        },
      });
    },
    async scrollIntoCurrentView() {
      await this.$nextTick();

      document.body.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.new-team {
  width: 100%;
}
#create-class-page {
  margin: 40px;
  width: 960px;
  @media only screen and (max-width: 768px) {
    margin: 0 30px;
  }
  h1 {
    border: 0;
  }
  .class-create-steps {
    margin-top: 25px;
  }
  .class-create-main {
    background-color: white;
    padding: 40px 0px;
  }
  ::v-deep .base-create-header {
    padding-bottom: 34px;
  }
  ::v-deep .circle-step {
    border: 1px solid #e6e6e6;
    box-sizing: border-box;
    border-radius: 23px;
    width: 40px;
    height: 40px;
    display: inline-flex;
    justify-content: center;
    padding-top: 8px;
    margin-right: 16px;
  }
}

.success-info {
  .success {
    font-weight: bold;
    font-size: 22px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #46c779;
    line-height: 30px;
  }
  p {
    margin-top: 20px;
    font-weight: 300;
    font-size: 18px;
    color: #546873;
    margin: 0;
  }
}
::v-deep .create-class-com-disable {
  opacity: 0.5;
}
.line-separate {
  border: 1px solid#E6E6E6;
  margin-bottom: 32px;
}
</style>
